import { useState } from "react"
import type { FormEvent } from "react"
import { CheckCircle2 } from "lucide-react"

import { Button } from "@/components/marketing/elements/button"
import { Section } from "@/components/marketing/elements/section"
import { MarketingAnchor } from "@/components/marketing/lib/anchor"
import { joinWaitlist } from "@/lib/marketing-rpc"
import { usePageTitle } from "@/lib/use-page-title"

import { Field, fieldClassName, joinWaitlistErrorMessage, useAccessFormLayout } from "./access-form"

export function WaitlistPage() {
  usePageTitle("Waitlist")

  const Layout = useAccessFormLayout()

  const [email, setEmail] = useState("")
  const [name, setName] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setError(null)
    setLoading(true)

    const { error: rpcError } = await joinWaitlist({ email, list: "waitlist", name })

    setLoading(false)

    if (rpcError) {
      setError(joinWaitlistErrorMessage(rpcError))
      return
    }

    setDone(true)
  }

  return (
    <Layout>
      <Section
        id="waitlist"
        eyebrow="General availability"
        headline="Get notified at launch"
        subheadline={<p>Leave your email and we&apos;ll let you know when ArtlessDev is open to everyone.</p>}
      >
        <div className="max-w-xl">
          {done ? (
            <div className="flex items-center gap-3 rounded-xl bg-verified/10 px-6 py-5 text-verified">
              <CheckCircle2 className="size-5 shrink-0" />
              <p className="text-base/6">
                You&apos;re on the list. We&apos;ll email you when ArtlessDev is generally available.
              </p>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              className="flex flex-col gap-5 rounded-xl bg-mauve-950/2.5 p-6 sm:p-8 dark:bg-white/5"
            >
              <Field label="Name" optional>
                <input
                  className={fieldClassName}
                  type="text"
                  name="name"
                  autoComplete="name"
                  placeholder="Jane Doe"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  disabled={loading}
                />
              </Field>
              <Field label="Email">
                <input
                  className={fieldClassName}
                  type="email"
                  name="email"
                  autoComplete="email"
                  required
                  placeholder="jane@company.com"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  disabled={loading}
                />
              </Field>
              {error ? <p className="text-sm text-destructive">{error}</p> : null}
              <Button type="submit" disabled={loading} size="lg" className="self-start">
                {loading ? "Joining…" : "Join the waitlist"}
              </Button>
            </form>
          )}
          <p className="mt-6 text-sm/6 text-mauve-600 dark:text-mauve-400">
            Want to start sooner? Access is open to a limited number of teams.{" "}
            <MarketingAnchor href="/request-access" className="font-medium underline underline-offset-4">
              Request access
            </MarketingAnchor>
          </p>
        </div>
      </Section>
    </Layout>
  )
}
